class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    /**
     * @type {HTMLButtonElement | null}
     */
    this.addToCartBtn = null;
    this.stickyContainer = null;
    /** @type {HTMLImageElement | null} */
    this.variantImage = null;
    /** @type {HTMLElement | null} */
    this.optionsWrapper = null;

    this.variantChangeHandler = null;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.clickHandler = null;
    this.extendedOfferEnabled = false;
    this.productJson = null;
  }

  connectedCallback() {
    this.setProductJson();

    // Find the external add-to-cart button
    this.addToCartBtn = document.querySelector(".product-form__submit");
    this.variantImage = /** @type {HTMLImageElement | null} */ (
      document.getElementById("StickyAddToCartImage")
    );
    this.optionsWrapper = document.querySelector(".variant-label-wrapper");

    // Find the sticky container (wrapper div)
    this.stickyContainer = document.getElementById("StickyAddToCartContainer");

    if (!this.addToCartBtn) {
      console.warn("product-form__submit button not found");
      return;
    }

    if (!this.stickyContainer) {
      console.warn("StickyAddToCartContainer not found");
      return;
    }

    // Bind handlers to preserve context
    this.scrollHandler = this.checkStickyVisibility.bind(this);
    this.resizeHandler = this.checkStickyVisibility.bind(this);

    // Add event listeners
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    window.addEventListener("resize", this.resizeHandler);

    // Add click handler for the sticky cart button
    this.clickHandler = this.handleStickyCartClick.bind(this);
    this.addEventListener("click", this.clickHandler);

    // Initial check
    this.checkStickyVisibility();

    this.variantChangeHandler = this.handleVariantChange.bind(this);
    messenger.listen(
      messenger.topics.VARIANT_CHANGE,
      this.variantChangeHandler,
      "StickyAddToCart"
    );

    this.variantChangeHandler(store.getLastVariantChange());

    this.mainAddToCartDisabled =
      this.addToCartBtn.classList.contains("disabled");

    if (this.mainAddToCartDisabled) {
      this.disableStickyCartButton();
    }

    messenger.listen(
      messenger.topics.PP_LIMIT_MESSAGE_SHOW,
      this.handlePPLimitMessageShow.bind(this),
      "StickyAddToCart"
    );
    messenger.listen(
      messenger.topics.PP_LIMIT_MESSAGE_HIDE,
      this.handlePPLimitMessageHide.bind(this),
      "StickyAddToCart"
    );
    messenger.listen(
      messenger.topics.PP_SETTING_UI,
      this.handlePPSettingUI.bind(this),
      "StickyAddToCart"
    );
    messenger.emmit(messenger.topics.STICKY_ADD_TO_CART_READY);
  }

  /**
   *
   * @param {Record<string, any> | null} data
   */
  handlePPSettingUI(data) {
    if (!data || !data.uiState) {
      return;
    }

    switch (data.uiState) {
      case "add-to-cart":
        this.setAddToCartUI();
        break;
      case "sold-out":
        this.setSoldOutUI();
        break;
      case "extended-offer":
        this.setExtendedOfferUI();
        break;
      default:
        console.log("no ui value found...");
    }
  }

  setAddToCartUI() {
    // console.log("setAddToCartUI");
    /**
     * @type {HTMLButtonElement | null}
     */
    const stickyButton = this.querySelector(".product-form__submit");
    if (!stickyButton) {
      return;
    }
    stickyButton.disabled = false;
    stickyButton.textContent = "Add to Cart";
    stickyButton.classList.remove("extended_offer__btn");
    this.extendedOfferEnabled = false;
  }

  setSoldOutUI() {
    // console.log("setSoldOutUI");
    /**
     * @type {HTMLButtonElement | null}
     */
    const stickyButton = this.querySelector(".product-form__submit");
    if (!stickyButton) {
      return;
    }
    stickyButton.disabled = true;
    stickyButton.textContent = "Sold Out";
    stickyButton.classList.remove("extended_offer__btn");
    this.extendedOfferEnabled = false;
  }

  setExtendedOfferUI() {
    // console.log("setExtendedOfferUI");
    /**
     * @type {HTMLButtonElement | null} stickyButton
     */
    const stickyButton = this.querySelector(".product-form__submit");
    if (!stickyButton) {
      console.log("no button found");
      return;
    }
    stickyButton.disabled = false;
    stickyButton.textContent = "Extended Offer";
    stickyButton.classList.add("extended_offer__btn");
    this.extendedOfferEnabled = true;
  }

  handlePPLimitMessageShow(event) {
    if (event.productId !== this.productJson.id.toString()) {
      return;
    }
    this.mainAddToCartDisabled = true;
    this.disableStickyCartButton();
  }

  handlePPLimitMessageHide(event) {
    if (event.productId !== this.productJson.id.toString()) {
      return;
    }
    this.mainAddToCartDisabled = false;
    this.enableStickyCartButton();
  }

  disableStickyCartButton() {
    /** @type {HTMLButtonElement | null} */
    const stickyButton = this.querySelector(".product-form__submit");
    if (!stickyButton) {
      return;
    }
    stickyButton.disabled = true;
  }

  enableStickyCartButton() {
    /** @type {HTMLButtonElement | null} */
    const stickyButton = this.querySelector(".product-form__submit");
    if (!stickyButton) {
      return;
    }
    stickyButton.disabled = false;
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    if (this.clickHandler) {
      this.removeEventListener("click", this.clickHandler);
    }
    if (this.variantChangeHandler) {
      messenger.unlisten(messenger.topics.VARIANT_CHANGE, "StickyAddToCart");
    }

    messenger.unlisten(
      messenger.topics.PP_LIMIT_MESSAGE_SHOW,
      "StickyAddToCart"
    );
    messenger.unlisten(
      messenger.topics.PP_LIMIT_MESSAGE_HIDE,
      "StickyAddToCart"
    );
  }

  handleVariantChange(/** @type {Variant} */ variant) {
    if (!variant) {
      console.log("variant not found");
      return;
    }

    // Update variant image only if available
    if (
      this.variantImage &&
      variant.featured_image &&
      variant.featured_image.src
    ) {
      this.variantImage.src = variant.featured_image.src;
      this.variantImage.alt = variant.featured_image.alt || variant.title;
    }

    // Always update variant options when variant exists
    if (this.optionsWrapper && variant.options) {
      this.optionsWrapper.innerHTML = variant.options
        .map((option) => `<span class="variant-label">${option}</span>`)
        .join("");
    }

    if (this.mainAddToCartDisabled) {
      this.disableStickyCartButton();
    } else {
      this.enableStickyCartButton();
    }
  }

  checkStickyVisibility() {
    if (!this.addToCartBtn || !this.stickyContainer) return;

    const rect = this.addToCartBtn.getBoundingClientRect();
    const isVisible = rect.bottom < 0 || rect.bottom < 60;
    const currentlyVisible =
      this.stickyContainer.classList.contains("sticky-visible");

    if (isVisible) {
      if (!currentlyVisible) {
        this.stickyContainer.classList.add("sticky-visible");
      }
    } else {
      if (currentlyVisible) {
        this.stickyContainer.classList.remove("sticky-visible");
      }
    }
  }

  handleStickyCartClick(event) {
    // Check if the click was on the add-to-cart button within the sticky cart
    const stickyButton = event.target.closest(".product-form__submit");
    const changeButton = event.target.closest(".variant-change");
    /**
     * @type {HTMLAnchorElement | null}
     */
    const extendedOfferBtn = document.querySelector(".extended_offer__btn");

    // console.log("extendedOfferEnabled", this.extendedOfferEnabled);
    // console.log("extendedOfferBtn", extendedOfferBtn);
    // console.log("stickyButton", stickyButton);
    // console.log("changeButton", changeButton);

    if (stickyButton && this.addToCartBtn) {
      // Prevent the default behavior of the sticky button
      event.preventDefault();
      event.stopPropagation();

      // Trigger the original add-to-cart button
      if (this.extendedOfferEnabled && extendedOfferBtn) {
        extendedOfferBtn.click();
      } else {
        const buttonLocationValue = this.addToCartBtn.dataset.buttonLocation;
        this.addToCartBtn.dataset.buttonLocation =
          "sticky-add-to-cart_product-page";
        this.addToCartBtn.click();
        this.addToCartBtn.dataset.buttonLocation = buttonLocationValue;
      }
    } else if (changeButton) {
      // Prevent the default behavior of the change link
      event.preventDefault();
      event.stopPropagation();

      // Scroll to the top of the page smoothly
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }

  setProductJson() {
    try {
      this.productJson = JSON.parse(
        document.getElementById("sticky-product-json").textContent
      );
    } catch (error) {
      console.error("Error parsing product JSON:", error);
    }
  }
}

// Register the custom element
customElements.define("sticky-add-to-cart", StickyAddToCart);
